import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { resolveInvoicePresignedUrl } from "../utils/invoiceUrlUtils";

const BackendInvoicePreviewModal = ({
	t,
	show,
	onHide,
	onClose,
	invoiceUrl,
	invoiceNo,
}) => {
	const iframeRef = useRef(null);
	const [presignedUrl, setPresignedUrl] = useState("");
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState("");

	useEffect(() => {
		if (!show || !invoiceUrl) {
			setPresignedUrl("");
			setLoadError("");
			return undefined;
		}

		let cancelled = false;

		const loadPresignedUrl = async () => {
			setLoading(true);
			setLoadError("");
			setPresignedUrl("");

			try {
				const url = await resolveInvoicePresignedUrl(invoiceUrl);
				if (cancelled) return;

				if (!url) {
					setLoadError(t("Invoice preview is not available."));
					return;
				}

				setPresignedUrl(url);
			} catch (error) {
				if (cancelled) return;
				setLoadError(
					error?.response?.data?.message ||
					t("Failed to load invoice preview. Please try again.")
				);
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		loadPresignedUrl();

		return () => {
			cancelled = true;
		};
	}, [show, invoiceUrl, t]);

	const handleDownload = () => {
		if (!presignedUrl) {
			toast.error(t("Download URL not available"));
			return;
		}

		try {
			const link = document.createElement("a");
			link.href = presignedUrl;
			link.target = "_blank";
			link.rel = "noopener noreferrer";
			link.download = "";  // Forces download
			link.style.display = "none";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			toast.success(t("Download started"));
		} catch (error) {
			console.error("Download failed:", error);
			toast.error(t("Failed to download. Please try again."));
		}
	};

	const handlePrint = () => {
		if (iframeRef.current?.contentWindow) {
			iframeRef.current.contentWindow.focus();
			iframeRef.current.contentWindow.print();
			return;
		}

		if (presignedUrl) {
			const printWindow = window.open(presignedUrl, "_blank", "noopener,noreferrer");
			printWindow?.focus();
		}
	};

	return (
		<Modal
			className="invoice-modal backend-invoice-preview-modal"
			show={show}
			onHide={onHide}
			scrollable
			centered
			size="xl"
			backdrop="static"
			keyboard={false}
		>
			<Modal.Header closeButton>
				<Modal.Title>
					{t("Invoice Preview")}
					{invoiceNo ? ` - ${invoiceNo}` : ""}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className="p-0">
				{loading && (
					<div className="d-flex justify-content-center align-items-center p-5">
						<Spinner animation="border" role="status" className="me-2" />
						<span>{t("Loading invoice...")}</span>
					</div>
				)}
				{!loading && loadError && (
					<div className="text-center p-5 text-muted">{loadError}</div>
				)}
				{!loading && presignedUrl && (
					<iframe
						ref={iframeRef}
						src={presignedUrl}
						title={t("Invoice PDF Preview")}
						style={{ width: "100%", minHeight: "70vh", border: "none" }}
					/>
				)}
			</Modal.Body>
			<Modal.Footer className="d-flex flex-wrap gap-2 justify-content-end">
				{/* <Button variant="primary" onClick={handleDownload} disabled={!presignedUrl}>
					<i className="fa fa-download me-2" />
					{t("Download")}
				</Button> */}
				{/* <Button variant="secondary" onClick={handlePrint} disabled={!presignedUrl}>
					<i className="fa fa-print me-2" />
					{t("Print")}
				</Button> */}
				<Button
					variant="light"
					onClick={() => {
						onClose?.();
					}}
				>
					{t("Close")}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default BackendInvoicePreviewModal;
